import * as React from "react";
import { AlertCircle } from "lucide-react";

import { cn } from "../../utils/cn";
import { Label } from "./label";

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  label?: string;
  error?: string;
  hint?: string;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
  wrapperClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      error,
      hint,
      required,
      suffix,
      prefix,
      id,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;

    const inputElement = (
      <div
        className={cn(
          "relative w-full",
          (suffix || prefix) && "flex items-center",
          wrapperClassName,
        )}
      >
        <input
          id={inputId}
          type={type}
          className={cn(
            "flex h-11 w-full rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-4 py-2 text-sm font-body text-surface-on shadow-ambient-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-outline/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-150 dark:bg-[#0D0E10] dark:border-[#434939] dark:text-white dark:placeholder:text-outline",
            error && "border-error focus-visible:ring-error",
            suffix && "pe-10",
            prefix && "ps-10",
            className,
          )}
          ref={ref}
          required={required}
          {...props}
        />
        {suffix && (
          <div className="absolute inset-e-3 top-1/2 -translate-y-1/2 flex items-center justify-center text-surface-on-variant">
            {suffix}
          </div>
        )}
        {prefix && (
          <div className="absolute inset-s-3 top-1/2 -translate-y-1/2 flex items-center justify-center text-surface-on-variant">
            {prefix}
          </div>
        )}
      </div>
    );

    if (!label && !error && !hint) {
      return inputElement;
    }

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {label && (
          <Label htmlFor={inputId} required={required}>
            {label}
          </Label>
        )}
        {inputElement}
        {error && (
          <p className="flex items-center gap-1.5 text-xs text-error font-label font-bold animate-fade-in">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            {error}
          </p>
        )}
        {hint && !error && (
          <p className="text-xs text-surface-on-variant dark:text-[#C3C9B4] font-body mt-1">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
